import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNotifier } from '../../../components/Notifier';
import {
  Box,
  Paper,
  Button,
  Typography,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  ListItemText,
  Checkbox,
  Select,
  OutlinedInput,
  CircularProgress,
  Popover,
  LinearProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Pagination
} from '@mui/material';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import HistoryIcon from '@mui/icons-material/History';

import InformationBubbleDialog from '../../../components/InformationBubbleDialog';
import CurrentDownloadVideoDialog from './CurrentDownloadVideoDialog';

import { apiHowen, apiVss } from '../../../api/axiosClient';
import { API_BASES, VSS_CONFIG } from '../../../config/constants';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 180
    }
  }
};
function replaceIpWithDomain(url) {
  if (!url) return url;
  // Match any IP optionally with port and replace with the configured HOST
  return url.replace(/[0-9.]+(:\d+)?/g, (match) => {
    // Basic IP shape check
    if (match.startsWith('103.169') || match.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/)) {
      return VSS_CONFIG.HOST;
    }
    return match;
  });
}
function formatTimeOnly(dateTimeStr) {
  if (!dateTimeStr) return '-';
  const parts = dateTimeStr.split(' ');
  return parts.length > 1 ? parts[1] : dateTimeStr;
}

const getDeviceStatusInfo = (accessMode) => {
  if (accessMode === undefined || accessMode === null) return { color: 'disabled', tooltip: 'Checking Status...' };
  switch (Number(accessMode)) {
    case -1:
      return { color: 'error', tooltip: 'Offline' };
    case 0:
      return { color: 'disabled', tooltip: 'Unknown' };
    case 1:
      return { color: '#01FF05', tooltip: 'Online (Ethernet)' };
    case 2:
      return { color: '#01FF05', tooltip: 'Online (WIFI)' };
    case 3:
      return { color: '#01FF05', tooltip: 'Online (2G)' };
    case 4:
      return { color: '#01FF05', tooltip: 'Online (3G)' };
    case 5:
      return { color: '#01FF05', tooltip: 'Online (4G)' };
    case 6:
      return { color: '#01FF05', tooltip: 'Online (5G)' };
    case 7:
      return { color: '#01FF05', tooltip: 'Online (WIFI+3/4/5G)' };
    case 8:
      return { color: '#01FF05', tooltip: 'Online (CABLE+3/4/5G)' };
    default:
      return { color: 'disabled', tooltip: 'Unknown' };
  }
};

const downloadFile = async (url, suggestedName, preventFallback = false) => {
  if (!url) return false;
  try {
    const resp = await fetch(url, { method: 'GET' });
    if (!resp.ok) {
      console.warn(`HTTP ${resp.status} - file may not exist or is unfinished`);
      return false;
    }
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    let filename = suggestedName || '';
    if (!filename) {
      try {
        const parts = url.split('/');
        filename = parts[parts.length - 1] || `download_${Date.now()}`;
      } catch {
        filename = `download_${Date.now()}`;
      }
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    return true;
  } catch (err) {
    console.warn('downloadFile failed:', err);
    if (preventFallback) return false;

    console.warn('fallback to open url directly');
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('fallback open failed', e);
    }
    return false;
  }
};

function HistoryVideoPageContent({ v: propV, onChangeSelected }) {
  const { t } = useLanguage();
  const location = useLocation();
  const notify = useNotifier();
  const navigate = useNavigate();
  const vehicle = propV ?? location.state?.vehicle ?? null;
  const vehicleList = useMemo(() => location.state?.vehicleList || [], [location.state]);
  const handleShowHistory = useCallback(() => {
    navigate(`/history-download-video?name=history-download-video`, {
      state: { vehicleList }
    });
  }, [navigate, vehicleList]);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 50;

  const filteredRows = useMemo(() => {
    if (allRows.length === 0) return [];
    const selectedChannelNumbers = selectedCameras
      .map((cam) => {
        const match = cam.match(/camera_(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((n) => n !== null);

    let nextFiltered = allRows;
    if (selectedChannelNumbers.length > 0) {
      nextFiltered = nextFiltered.filter((row) => {
        const rowChannel = parseInt(row.rawChannel, 10);
        return selectedChannelNumbers.includes(rowChannel);
      });
    }
    return nextFiltered;
  }, [allRows, selectedCameras]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page]);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(-1);

  useEffect(() => {
    setCurrentVideoIndex(filteredRows.length > 0 ? 0 : -1);
    setPage(1);
  }, [filteredRows]);

  useEffect(() => {
    if (currentVideoIndex >= 0) {
      setIframeLoading(true);
    }
  }, [currentVideoIndex]);
  const [videoSpeed, setVideoSpeed] = useState(1);
  const [loading, setLoading] = useState(!!vehicle);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(location.state?.token ?? '');
  const [openInfo, setOpenInfo] = useState(false);
  const [openCurrentDownload, setOpenCurrentDownload] = useState(false);
  const [deviceStatusData, setDeviceStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchDeviceStatus = useCallback(() => {
    const imei = String(
      vehicle?.integration_imei || (vehicle?.raw && vehicle.raw.integration_imei) || (vehicle?.info && vehicle.info.integration_imei) || ''
    ).trim();
    if (!token || !imei || imei === 'Camera not installed') return;

    setStatusLoading(true);
    apiVss
      .post('/onoffline/queryDeviceStatus.action', {
        token: String(token),
        deviceID: imei
      })
      .then((resp) => {
        const data = resp.data?.data || resp.data;
        if (Array.isArray(data)) {
          const item = data.find((i) => String(i.deviceID || i.id) === imei);
          if (item) setDeviceStatusData(item);
        } else if (data && typeof data === 'object') {
          setDeviceStatusData(data);
        }
      })
      .catch((err) => console.error('Failed to fetch device status', err))
      .finally(() => setStatusLoading(false));
  }, [token, vehicle]);

  useEffect(() => {
    fetchDeviceStatus();
    const interval = setInterval(() => {
      fetchDeviceStatus();
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchDeviceStatus]);

  const [anchorElFilter, setAnchorElFilter] = useState(null);

  const [anchorElDownload, setAnchorElDownload] = useState(null);
  const [downloadTask, setDownloadTask] = useState(null);
  const downloadTimerRef = useRef(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [installedCameras, setInstalledCameras] = useState([]);

  useEffect(() => {
    if (!token) return;
    const cached = localStorage.getItem('vehicle_installed_camera');
    if (cached) {
      try {
        setInstalledCameras(JSON.parse(cached));
        return;
      } catch {}
    }

    const imeiArray = (vehicleList || [])
      .map((v) => v.integration_imei || (v.raw && v.raw.integration_imei) || (v.info && v.info.integration_imei))
      .filter((imei) => imei && String(imei).trim() !== '' && String(imei).trim() !== 'Camera not installed');

    const uniqueImeis = [...new Set(imeiArray)];
    if (uniqueImeis.length === 0) return;

    Promise.all(
      uniqueImeis.map((imei) => {
        const params = new URLSearchParams();
        params.append('pageNum', '-1');
        params.append('pageCount', '-1');
        params.append('token', String(token));
        params.append('scheme', 'https');
        params.append('lang', 'en_US');
        params.append('keyword', imei);

        return apiVss
          .post('/vehicle/findAll.action', params, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          })
          .then((res) => {
            const responseData = res.data?.data || res.data;
            const dataList = responseData?.dataList || responseData;
            if (Array.isArray(dataList)) {
              return dataList.map((item) => ({
                deviceno: item.deviceno || item.imei,
                channelname: item.channelname
              }));
            }
            return [];
          })
          .catch((err) => {
            console.error(`Failed to fetch cameras for ${imei}`, err);
            return [];
          });
      })
    ).then((results) => {
      const combined = results.flat();
      if (combined.length > 0) {
        localStorage.setItem('vehicle_installed_camera', JSON.stringify(combined));
        setInstalledCameras(combined);
      }
    });
  }, [token, vehicleList]);

  const cameraOptions = useMemo(() => {
    const imei = String(
      vehicle?.integration_imei || (vehicle?.raw && vehicle.raw.integration_imei) || (vehicle?.info && vehicle.info.integration_imei) || ''
    ).trim();
    const installed = installedCameras.find((c) => String(c.deviceno) === imei);
    if (installed && installed.channelname) {
      const channels = installed.channelname.split(';').filter((ch) => ch.trim() !== '');
      if (channels.length > 0) {
        return channels.map((ch, index) => {
          const num = ch.replace(/\D/g, '') || index + 1;
          return { value: `camera_${num}`, label: `Camera ${num}` };
        });
      }
    }
    return [
      { value: 'camera_1', label: 'Camera 1' },
      { value: 'camera_2', label: 'Camera 2' },
      { value: 'camera_3', label: 'Camera 3' },
      { value: 'camera_4', label: 'Camera 4' }
    ];
  }, [installedCameras, vehicle]);

  const handleFilterClick = (event) => setAnchorElFilter(event.currentTarget);
  const handleFilterClose = () => setAnchorElFilter(null);

  const handleApplyFilter = () => {
    if (!vehicle) return;

    const formattedStart = `${selectedDate.format('YYYY-MM-DD')} 00:00:00`;
    const formattedEnd = `${selectedDate.format('YYYY-MM-DD')} 23:59:59`;

    fetchList({
      deviceID: vehicle.integration_imei ?? vehicle.deviceID ?? vehicle.id,
      startTime: formattedStart,
      endTime: formattedEnd
    });
    handleFilterClose();
  };

  const handleCameraChange = (event) => {
    const value = event.target.value;
    if (value.includes('all')) {
      if (selectedCameras.length === cameraOptions.length) {
        setSelectedCameras([]);
        if (typeof onChangeSelected === 'function' && vehicle)
          onChangeSelected(String(vehicle.id ?? vehicle.vehicleId ?? vehicle.integration_imei ?? ''), []);
      } else {
        const allCams = cameraOptions.map((o) => String(o.value));
        setSelectedCameras(allCams);
        if (typeof onChangeSelected === 'function' && vehicle)
          onChangeSelected(String(vehicle.id ?? vehicle.vehicleId ?? vehicle.integration_imei ?? ''), allCams);
      }
    } else {
      const arr = Array.isArray(value) ? value.map((x) => String(x)).filter((x) => x !== 'all') : [String(value)];
      setSelectedCameras(arr);
      if (typeof onChangeSelected === 'function' && vehicle)
        onChangeSelected(String(vehicle.id ?? vehicle.vehicleId ?? vehicle.integration_imei ?? ''), arr);
    }
  };

  const buildPayloadForSearch = useCallback(
    ({ deviceIdOverride, startTime, endTime, channelList, token: argToken } = {}) => {
      const deviceID = deviceIdOverride ?? vehicle?.integration_imei ?? vehicle?.deviceID ?? vehicle?.id ?? '';
      const channels =
        channelList ??
        (Array.isArray(selectedCameras) && selectedCameras.length > 0
          ? selectedCameras
              .map((s) => {
                const m = String(s).match(/(\d+)/);
                return m ? m[1] : null;
              })
              .filter(Boolean)
              .join(';')
          : '1;2');

      const now = new Date();
      const defaultStart =
        startTime ??
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} 00:00:00`;
      const defaultEnd =
        endTime ?? `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} 23:59:59`;

      const payload = {
        channelList: channels,
        deviceID: deviceID,
        startTime: defaultStart,
        endTime: defaultEnd,
        fileType: '1',
        lang: 'en_US',
        location: 1,
        scheme: 'https',
        stream: 1
      };
      const activeToken = argToken || token;
      if (activeToken) payload.token = String(activeToken);
      return payload;
    },
    [vehicle, selectedCameras, token]
  );

  const fetchList = useCallback(
    async (opts = {}) => {
      setLoading(true);
      setError(null);
      try {
        let t = opts.token ?? token;
        if (!t) {
          try {
            const tkResp = await apiHowen.get('/get-token');
            t = tkResp?.data?.token ?? tkResp?.data?.data?.token ?? tkResp?.data ?? null;
            if (t) setToken(String(t));
          } catch (e) {
            console.warn('failed to load token', e);
          }
        }

        const payload = buildPayloadForSearch({ ...opts, token: t });
        const resp = await apiVss.post('/record/videoFileSearch.action', payload, {
          headers: { 'Content-Type': 'application/json' }
        });

        const body = resp?.data ?? resp;
        const files = Array.isArray(body?.data?.files) ? body.data.files : Array.isArray(body?.files) ? body.files : [];
        const mapped = (files || []).map((f, i) => {
          const id =
            f.path && typeof f.path === 'string' && f.path.split('/').length ? f.path.split('/').pop() : (f.fileGuid ?? `row-${i}`);
          let rawVideoUrl = f.downUrl;
          if (!rawVideoUrl && f.path) {
            rawVideoUrl = `${API_BASES.VSS.replace('/vss', '')}/fileSrv${f.path.startsWith('/') ? '' : '/'}${f.path}`;
          }

          const finalVideoUrl = replaceIpWithDomain(rawVideoUrl);
          // console.log('Constructed Video URL for Type 1/4:', finalVideoUrl);

          return {
            id,
            plate_number: f.deviceName ?? f.deviceID ?? '-',
            deviceId: f.deviceID ?? '-',
            vehicle_type: f.type != null ? String(f.type) : '-',
            vehicle_category: '-',
            rawChannel: f.channel,
            camera: f.channel != null ? `Channel ${f.channel}` : '-',
            start: f.start ?? '',
            stop: f.stop ?? '',
            download_date: (f.start ?? '').split(' ')[0] ?? '',
            username: f.username ?? '-',
            video: finalVideoUrl ?? '',
            hasDownUrl: !!f.downUrl
          };
        });
        mapped.sort((a, b) => {
          const dateA = new Date(a.start);
          const dateB = new Date(b.start);
          return dateA - dateB;
        });
        setAllRows(mapped);

        if (mapped.length > 0) {
          setCurrentVideoIndex((prev) => (prev !== -1 ? prev : 0));
        } else {
          setCurrentVideoIndex(-1);
        }
      } catch (err) {
        console.error('videoFileSearch error', err);
        setError(String(err?.message ?? err));
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    },
    [buildPayloadForSearch, token]
  );
  useEffect(() => {
    if (!vehicle) {
      setAllRows([]);
      setCurrentVideoIndex(-1);
      return;
    }
    fetchList({ deviceID: vehicle.integration_imei ?? vehicle.deviceID ?? vehicle.id ?? undefined });
  }, [vehicle, fetchList]);
  const handleNextVideo = () => {
    if (currentVideoIndex < filteredRows.length - 1) {
      setCurrentVideoIndex((prev) => prev + 1);
    }
  };

  const handlePrevVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex((prev) => prev - 1);
    }
  };

  useEffect(() => {
    if (currentVideoIndex >= 0) {
      const el = document.getElementById(`video-item-${currentVideoIndex}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [currentVideoIndex]);

  const handleDirectDownload = async (event, row) => {
    event.stopPropagation();
    const fileName = `${row.plate_number}_${dayjs(row.start).format('YYYYMMDD_HHmmss')}.mp4`;
    const success = await downloadFile(row.video, fileName);
    if (success) {
      notify({ message: t('liveStreaming.downloadSuccess', 'Video downloaded successfully'), severity: 'success' });
    } else {
      notify({ message: t('liveStreaming.failedDownload', 'Failed to download video. The target file may not exist.'), severity: 'error' });
    }
  };

  const handleInitDownload = async (event, row) => {
    event.stopPropagation();
    setAnchorElDownload(event.currentTarget);
    setDownloadTask({ status: 'running', progress: 0, taskId: null, fileData: row });
    try {
      const payloadInsert = {
        username: 'admin',
        deviceNo: row.deviceId,
        channel: String(row.rawChannel || 1).replace(/\D/g, ''),
        resStartTime: row.start,
        resEndTime: row.stop,
        resDeviceFileName: '',
        fileFormat: 'mp4',
        scheme: 'https',
        token: token,
        lang: 'en_US'
      };

      const res = await apiVss.post('/webdownrecord/insert.action', payloadInsert, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = res.data;

      if (data && data.status === 10000 && data.data?.taskId) {
        const taskId = data.data.taskId;
        const rawPath = data.data.resWebPath;
        try {
          const payloadTask = {
            taskId: taskId,
            token: token,
            action: 1,
            username: 'admin',
            resWebPath: rawPath
          };

          await apiVss.post('/webdownrecord/taskManagement.action', payloadTask, {
            headers: { 'Content-Type': 'application/json' }
          });
        } catch (e) {
          console.warn('Trigger Resume failed, polling might stuck at 0', e);
        }
        setDownloadTask((prev) => ({ ...prev, taskId: taskId, progress: 1 }));
        startPollingProgress(taskId, row, rawPath);
      } else {
        throw new Error(data?.msg || 'Gagal membuat tugas unduhan');
      }
    } catch (err) {
      console.error('Download init error', err);
      setDownloadTask((prev) => ({ ...prev, status: 'error', errorMsg: err.message }));
    }
  };

  const startPollingProgress = (taskId, rowData, resWebPath) => {
    if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);

    downloadTimerRef.current = setInterval(async () => {
      try {
        const res = await apiVss.post(
          '/webdownrecord/findTaskProgress.action',
          {
            taskId: taskId,
            token: token,
            resWebPath: resWebPath
          },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const data = res.data;

        if (data && data.status === 10000) {
          const progress = data.data.progress || 0;
          const state = data.data.downState;
          setDownloadTask((prev) => ({
            ...prev,
            progress: progress,
            stateCode: state
          }));

          if (progress >= 100 || state === 2) {
            clearInterval(downloadTimerRef.current);
            const rawFinalUrl = data.data.downUrl || rowData.video;
            const finalUrl = replaceIpWithDomain(rawFinalUrl);
            setDownloadTask((prev) => ({
              ...prev,
              status: 'finished',
              progress: 100,
              resultUrl: finalUrl
            }));
          } else if (state === -1 || state === -9 || state < 0) {
            clearInterval(downloadTimerRef.current);
            const rawFinalUrl = data.data.downUrl || rowData.video;
            const finalUrl = replaceIpWithDomain(rawFinalUrl);
            let errorMsg = 'Download Failed/Stopped on Server';
            if (state === -1) errorMsg = t('liveStreaming.pauseState', 'Pause State');
            else if (state === -7) errorMsg = t('liveStreaming.deviceNodata', 'Device no data');
            else if (state === -8) errorMsg = t('liveStreaming.deviceOffline', 'Device offline');
            else if (state === -9) errorMsg = t('liveStreaming.abnormalStop', 'Abnormal Stop State');

            setDownloadTask((prev) => ({
              ...prev,
              status: 'error',
              errorMsg: errorMsg,
              resultUrl: finalUrl
            }));
          }
        }
      } catch (err) {
        console.error('Polling error', err);
      }
    }, 1500);
  };
  useEffect(() => {
    return () => {
      if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);
    };
  }, []);

  const handleCloseDownloadPopover = () => {
    if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);
    setAnchorElDownload(null);
    setDownloadTask(null);
  };

  const handleReload = () => {
    if (!vehicle) return;
    fetchList({ deviceID: vehicle.integration_imei ?? vehicle.deviceID ?? vehicle.id ?? undefined });
    fetchDeviceStatus();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)' }}>
        <Box sx={{ flexShrink: 0, mb: 1 }}>
          <Button
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => navigate('/live-streaming?name=live-streaming')}
            size="small"
            sx={{
              color: 'primary',
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
            }}
          >
            {t('buttonTitle.back', 'Back')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', width: '70%', flexDirection: 'column', height: '100%' }}>
            <Paper sx={{ display: 'flex', width: 1, p: 1, flexGrow: 1, overflow: 'hidden', bgcolor: '#000' }}>
              {(() => {
                const currentData = filteredRows[currentVideoIndex];
                if (!currentData || !token) {
                  return (
                    <Box
                      sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                    >
                      <Typography variant="body1">
                        {t('liveStreaming.selectVideoToPlay', 'Select a video from the list to play.')}
                      </Typography>
                    </Box>
                  );
                }

                const deviceId = currentData.deviceId ?? currentData.deviceID ?? vehicle?.integration_imei ?? vehicle?.deviceID;
                const chs = currentData.rawChannel ?? currentData.channel ?? '1';
                const st = (currentData.start || '').replace(/[- :]/g, '');
                const et = (currentData.stop || '').replace(/[- :]/g, '');
                const iframeSrc = `${API_BASES.VSS}/apiPage/ReplayVideo.html?token=${token}&deviceId=${deviceId}&chs=${chs}&st=${st}&et=${et}&wnum=1&panel=0&buffer=3000&speed=${videoSpeed}`;

                // console.log('Iframe Video URL:', iframeSrc);

                return (
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    {iframeLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.5)',
                          zIndex: 10
                        }}
                      >
                        <CircularProgress color="primary" />
                      </Box>
                    )}
                    <iframe
                      key={filteredRows[currentVideoIndex].id}
                      src={iframeSrc}
                      style={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
                      title="History Video"
                      onLoad={() => setIframeLoading(false)}
                    />
                  </Box>
                );
              })()}
            </Paper>
            <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, p: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<SkipPreviousIcon />}
                  onClick={handlePrevVideo}
                  disabled={currentVideoIndex <= 0}
                  variant="outlined"
                  size="small"
                >
                  Previous
                </Button>
                <Button
                  endIcon={<SkipNextIcon />}
                  onClick={handleNextVideo}
                  disabled={currentVideoIndex === -1 || currentVideoIndex >= filteredRows.length - 1}
                  variant="outlined"
                  size="small"
                >
                  Next
                </Button>
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Speed</InputLabel>
                <Select value={videoSpeed} label="Speed" onChange={(e) => setVideoSpeed(e.target.value)}>
                  <MenuItem value={1}>1x</MenuItem>
                  <MenuItem value={4}>4x</MenuItem>
                  <MenuItem value={8}>8x</MenuItem>
                  <MenuItem value={16}>16x</MenuItem>
                </Select>
              </FormControl>
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', width: '30%', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleShowHistory}
                sx={{ p: 1, flexGrow: 1, bgcolor: '#0374DA', color: '#ffff', '&:hover': { bgcolor: '#0889faff' } }}
              >
                {t('liveStreaming.downloadHistory', 'Download Video History')}
              </Button>
              <IconButton onClick={() => setOpenInfo(true)} sx={{ bgcolor: '#e3f2fd', color: '#0374DA', borderRadius: 2 }}>
                <InfoOutlinedIcon />
              </IconButton>
            </Box>

            <Paper sx={{ mt: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
              <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ p: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 500 }}>
                          {vehicle?.vehicle_name ?? vehicle?.fleet_name ?? vehicle?.plate_number ?? 'Vehicle'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <LocalShippingOutlinedIcon fontSize="small" sx={{ color: '#00008B' }} />
                          <Typography variant="body1">{vehicle?.vehicle_type ?? vehicle?.vehicle_type_name ?? 'Type Vehicle'}</Typography>
                        </Stack>
                      </Box>
                      <Box>
                        {(() => {
                          const imei = String(
                            vehicle?.integration_imei ||
                              (vehicle?.raw && vehicle.raw.integration_imei) ||
                              (vehicle?.info && vehicle.info.integration_imei) ||
                              ''
                          ).trim();
                          if (!imei || imei === 'Camera not installed') return null;
                          if (statusLoading) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 41, height: 41 }}>
                                <CircularProgress size={20} />
                              </Box>
                            );
                          }
                          const accessMode = deviceStatusData?.accessMode;
                          const statusInfo = getDeviceStatusInfo(accessMode);
                          const titleNode = (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {statusInfo.tooltip}
                              </Typography>
                              {deviceStatusData?.lastOnlineTime && (
                                <Typography variant="caption" display="block">
                                  Last Online: {deviceStatusData.lastOnlineTime}
                                </Typography>
                              )}
                              {deviceStatusData?.lastOfflineTime && (
                                <Typography variant="caption" display="block">
                                  Last Offline: {deviceStatusData.lastOfflineTime}
                                </Typography>
                              )}
                            </Box>
                          );
                          return (
                            <Tooltip title={titleNode}>
                              <Button
                                sx={{
                                  width: 41,
                                  height: 41,
                                  minWidth: 41,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  p: 0
                                }}
                              >
                                <VideocamOutlinedIcon sx={{ color: statusInfo.color, fontWeight: 700 }} />
                              </Button>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box>
                        <Typography variant="body1">{t('liveStreaming.videoPeriod', 'Periode Video')}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {selectedDate.format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('liveStreaming.todaysVideo', "Today's Download History")}>
                          <Button
                            variant="outlined"
                            onClick={() => setOpenCurrentDownload(true)}
                            sx={{
                              width: 41,
                              height: 41,
                              minWidth: 41,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0,
                              borderColor: '#485D9A',
                              '&:hover': { borderColor: '#485D9A' }
                            }}
                          >
                            <HistoryIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
                          </Button>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          onClick={handleFilterClick}
                          sx={{
                            width: 41,
                            height: 41,
                            minWidth: 41,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0,
                            borderColor: '#485D9A',
                            '&:hover': { borderColor: '#485D9A' }
                          }}
                        >
                          <FilterAltOutlinedIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
                        </Button>
                      </Box>
                      <Popover
                        open={Boolean(anchorElFilter)}
                        anchorEl={anchorElFilter}
                        onClose={handleFilterClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left'
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                      >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 250 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {t('liveStreaming.videoPeriod', 'Video Period')}
                          </Typography>

                          <DatePicker
                            label={t('time.date', 'Date')}
                            value={selectedDate}
                            onChange={(newValue) => setSelectedDate(newValue)}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          />
                          <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#485D9A' }}>
                            {t('dashboardFuel.search', 'Search')}
                          </Button>
                        </Box>
                      </Popover>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 1, border: 1 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <FormControl sx={{ minWidth: 180, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                      <InputLabel id={`camera-select-label-${vehicle?.id ?? 'v'}`}>
                        {t('liveStreaming.selectCamera', 'Pilih Camera')}
                      </InputLabel>
                      <Select
                        labelId={`camera-select-label-${vehicle?.id ?? 'v'}`}
                        multiple
                        value={selectedCameras}
                        onChange={handleCameraChange}
                        input={<OutlinedInput label={t('liveStreaming.selectCamera', 'Pilih Camera')} />}
                        renderValue={(selected) => {
                          if (selected.length === 0) return '—';
                          const selectedLabels = selected.map((val) => {
                            const option = cameraOptions.find((opt) => opt.value === val);
                            return option ? option.label : val;
                          });

                          return selectedLabels.slice(0, 2).join(', ') + (selected.length > 2 ? ` (+${selected.length - 2})` : '');
                        }}
                        MenuProps={MenuProps}
                        size="small"
                      >
                        <MenuItem value="all">
                          <Checkbox checked={selectedCameras.length > 0 && selectedCameras.length === cameraOptions.length} />
                          <ListItemText primary="Semua Camera" />
                        </MenuItem>
                        {cameraOptions.map((opt) => {
                          const val = String(opt.value);
                          return (
                            <MenuItem key={val} value={val}>
                              <Checkbox checked={selectedCameras.indexOf(val) > -1} />
                              <ListItemText primary={opt.label} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <Button
                      onClick={handleReload}
                      sx={{ p: 1, width: 120, bgcolor: '#0374DA', color: '#ffff', '&:hover': { bgcolor: '#0889faff' } }}
                    >
                      {t('buttonTitle.reload', 'Reload')}
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('liveStreaming.lastUpdate', 'Update Terakhir :')}{' '}
                    {allRows.length > 0
                      ? dayjs(allRows[allRows.length - 1].start).format('DD/MM/YYYY HH:mm:ss')
                      : new Date().toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('general.totalVideo', 'Total Video')}: {filteredRows.length}
                  </Typography>
                </Box>

                <Divider />
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}
              >
                <Box>
                  {error && (
                    <Box sx={{ p: 2 }}>
                      <Typography color="error">Error: {error}</Typography>
                    </Box>
                  )}
                  {filteredRows.length === 0 && !loading && (
                    <Box
                      sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        minHeight: 250
                      }}
                    >
                      <VideocamOffOutlinedIcon sx={{ fontSize: 64, mb: 2, color: '#9e9e9e' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#424242', mb: 1 }}>
                        {t('liveStreaming.noVideoFound', 'No videos found')}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 350, color: '#757575' }}>
                        {t(
                          'liveStreaming.noVideoRecord',
                          'No video records were found. This is often due to a poor network signal on the vehicles device, or there are simply'
                        )}
                        {t('liveStreaming.noRecording', 'no recordings for the selected date range.')}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 1, pb: 1 }}>
                    {loading ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box>
                        {paginatedRows.map((r, idx) => {
                          const absoluteIdx = (page - 1) * rowsPerPage + idx;
                          return (
                            <Box
                              key={r.id}
                              id={`video-item-${absoluteIdx}`}
                              onClick={() => setCurrentVideoIndex(absoluteIdx)}
                              sx={{
                                p: 1,
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                bgcolor: currentVideoIndex === absoluteIdx ? '#e3f2fd' : 'transparent',
                                border: currentVideoIndex === absoluteIdx ? '2px solid #0374DA' : 'none',
                                '&:hover': { bgcolor: currentVideoIndex === absoluteIdx ? '#e3f2fd' : '#f5f5f5' }
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {dayjs(r.start).format('DD/MM/YYYY')}
                                </Typography>
                                <Typography variant="body2">
                                  {formatTimeOnly(r.start)} - {formatTimeOnly(r.stop)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {r.camera}
                                </Typography>
                              </Box>
                              <IconButton
                                color="primary"
                                onClick={(e) => {
                                  if (r.hasDownUrl) {
                                    handleDirectDownload(e, r);
                                  } else {
                                    handleInitDownload(e, r);
                                  }
                                }}
                              >
                                <SaveAltOutlinedIcon />
                              </IconButton>
                            </Box>
                          );
                        })}
                        {filteredRows.length > rowsPerPage && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mt: 1 }}>
                            <Pagination
                              count={Math.ceil(filteredRows.length / rowsPerPage)}
                              page={page}
                              onChange={(e, val) => setPage(val)}
                              color="primary"
                              size="small"
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorElDownload)}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownloadPopover}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('liveStreaming.downloadStatus', 'Download Status')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {downloadTask?.status === 'running' && (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {downloadTask.stateCode === -8
                  ? t('liveStreaming.waitingForVideo', 'Waiting for the Device to Come Online...')
                  : downloadTask.stateCode === -7
                    ? t('liveStreaming.dataNotFound', 'Data Not Found on Device')
                    : downloadTask.stateCode === 0
                      ? t('liveStreaming.waitingInLine', 'Waiting in Line...')
                      : t('liveStreaming.downloading', 'Downloading from Device...')}
              </Typography>

              {downloadTask.stateCode === -8 && (
                <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                  {t('liveStreaming.makeSure', 'Make sure the vehicle is on and has a signal.')}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress variant="determinate" value={downloadTask.progress} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="text.secondary">{`${Math.round(downloadTask.progress)}%`}</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {downloadTask?.status === 'finished' && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 50, mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {t('liveStreaming.videoReady', 'Video Siap Diunduh!')}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveAltOutlinedIcon />}
                  onClick={() => {
                    handleCloseDownloadPopover();
                    setOpenCurrentDownload(true);
                  }}
                >
                  {t('buttonTitle.openDownload', 'Buka Hasil Unduhan')}
                </Button>
              </Stack>
            </Box>
          )}
          {downloadTask?.status === 'error' && (
            <Box sx={{ textAlign: 'center' }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 50, mb: 1 }} />
              <Typography variant="body1">{t('liveStreaming.failedPrepareVideo', 'Gagal Menyiapkan Video')}</Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                {downloadTask.errorMsg}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      <Dialog open={Boolean(previewVideoUrl)} onClose={() => setPreviewVideoUrl(null)} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 0 }}>
          <Typography variant="h6">{t('liveStreaming.previewVideo', 'Preview Video')}</Typography>
          <IconButton onClick={() => setPreviewVideoUrl(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          {previewVideoUrl && (
            <video controls autoPlay style={{ width: '100%', maxHeight: '70vh' }}>
              <source src={previewVideoUrl} type="video/mp4" />
              {t('liveStreaming.browserNotSupportVideo', 'Browser tidak mendukung video tag.')}
            </video>
          )}
        </DialogContent>
      </Dialog>
      <InformationBubbleDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t('answer.titleHistoryVideo', 'Download Information')}
        messages={[
          { type: 'q', text: t('question.historyVideo1', 'What happens when I download a video?') },
          {
            type: 'a',
            text: t(
              'answer.historyVideo1',
              'The system will request the video file directly from the device in the vehicle. This process requires the device to be active.'
            )
          },
          { type: 'q', text: t('question.historyVideo2', 'Why would the download fail?') },
          {
            type: 'a',
            text: t(
              'answer.historyVideo2',
              'Usually, this happens if the vehicle is turned off, the camera is disconnected, or the device loses its internet signal.'
            )
          },
          { type: 'q', text: t('question.historyVideo3', 'What is the solution?') },
          {
            type: 'a',
            text: t(
              'answer.historyVideo3',
              'Please ensure the vehicle engine is turned on and the device has a stable connection before you start downloading.'
            )
          },
          { type: 'q', text: t('question.historyVideo4', 'Can I adjust the video speed?') },
          {
            type: 'a',
            text: t(
              'answer.historyVideo4',
              'Yes, you can easily adjust the playback speed of the videos directly on this History Video Page.'
            )
          },
          { type: 'q', text: t('question.historyVideo5', 'Where can I see the downloaded videos?') },
          {
            type: 'a',
            text: t('answer.historyVideo5', 'Downloaded videos are available in the History Today and History Download Video Page.')
          }
        ]}
      />
      <CurrentDownloadVideoDialog
        open={openCurrentDownload}
        onClose={() => setOpenCurrentDownload(false)}
        vehicle={vehicle}
        token={token}
      />
    </LocalizationProvider>
  );
}
import Breadcrumbs from '../../../components/@extended/Breadcrumbs';
export default function HistoryVideoPage({ vehicleList = [] }) {
  const vehicle = Array.isArray(vehicleList) ? vehicleList[0] : vehicleList;
  return (
    <>
      <Breadcrumbs
        custom
        heading="History Video"
        links={[
          { title: 'Dashboard', to: '/dashboard' },
          { title: 'Live Streaming', to: '/live-streaming?name=live-streaming' },
          { title: 'History Video' }
        ]}
      />
      <HistoryVideoPageContent v={vehicle} />
    </>
  );
}
